import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Eye, Loader2, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CreatorVideo() {
  const { id } = useParams();
  const { isAuthed } = useAuth();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [counted, setCounted] = useState(false);
  const watchRef = useRef(0);

  useEffect(() => {
    endpoints.creatorVideo(id).then((r) => setV(r.data)).catch(() => {}).finally(() => setLoading(false));
    endpoints.creatorVideoComments(id).then((r) => setComments(r.data?.comments || [])).catch(() => {});
  }, [id]);

  // Verified eligible view: only for signed-in users, only after a real minimum watch time.
  // Refreshing never re-counts (the server dedups one eligible view per user per video).
  useEffect(() => {
    if (!isAuthed || counted) return;
    const t = setInterval(() => {
      watchRef.current += 1;
      if (watchRef.current >= 35) {
        clearInterval(t);
        endpoints.creatorVideoView(id, watchRef.current)
          .then((r) => { if (r.data?.counted) { setCounted(true); setV((p) => p ? { ...p, eligibleViews: (p.eligibleViews || 0) + 1 } : p); } else if (r.data?.eligible) { setCounted(true); } })
          .catch(() => {});
      }
    }, 1000);
    return () => clearInterval(t);
  }, [isAuthed, counted, id]);

  const postComment = async (e) => {
    e.preventDefault();
    if (!isAuthed) { window.dispatchEvent(new Event("open-auth")); return; }
    if (!text.trim()) return;
    try {
      const r = await endpoints.creatorVideoAddComment(id, text.trim());
      setComments((c) => [r.data, ...c]);
      setText("");
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  if (!v) return <div className="text-center py-32 text-slate-400">Video not found.</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="creator-video-page">
      {v.creator && <Link to={`/creators/${v.creator.slug}`} className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4"><ArrowLeft className="w-4 h-4" /> {v.creator.name}</Link>}
      <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-[#273042]">
        <iframe title={v.title} src={v.embed} className="w-full h-full"
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" data-testid="creator-video-player" />
      </div>
      <div className="flex items-start justify-between gap-4 mt-4">
        <h1 className="text-xl sm:text-2xl font-bold text-white">{v.title}</h1>
        <span className="shrink-0 text-sm text-slate-400 flex items-center gap-1.5 mt-1" data-testid="video-view-count"><Eye className="w-4 h-4 text-[#E11D2A]" /> {(v.eligibleViews || 0).toLocaleString()}</span>
      </div>
      {counted && <p className="text-xs text-green-400 flex items-center gap-1.5 mt-1" data-testid="view-counted"><CheckCircle2 className="w-3.5 h-3.5" /> Your view was counted — thanks for supporting {v.creator?.name}!</p>}
      {!isAuthed && <p className="text-xs text-slate-500 mt-1">Sign in so your watch helps this creator earn.</p>}
      {v.description && <p className="text-slate-400 text-sm mt-3 whitespace-pre-line">{v.description}</p>}

      <div className="mt-10">
        <h2 className="text-lg font-bold text-white mb-4">Comments ({comments.length})</h2>
        <form onSubmit={postComment} className="flex gap-2 mb-6">
          <input value={text} onChange={(e) => setText(e.target.value)} data-testid="comment-input"
            placeholder={isAuthed ? "Add a comment…" : "Sign in to comment"}
            className="flex-1 h-11 px-4 rounded-full bg-[#121620] border border-[#273042] text-white placeholder-slate-500 focus:border-[#E11D2A] outline-none" />
          <button type="submit" data-testid="comment-submit" className="h-11 w-11 rounded-full bg-[#E11D2A] text-white flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
        </form>
        <div className="space-y-4">
          {comments.map((m) => (
            <div key={m.id} className="flex gap-3" data-testid={`comment-${m.id}`}>
              <span className="w-9 h-9 rounded-full bg-[#E11D2A] text-white text-xs font-bold flex items-center justify-center shrink-0">{(m.name || "U").slice(0, 2).toUpperCase()}</span>
              <div>
                <p className="text-sm text-white font-semibold">{m.name}</p>
                <p className="text-sm text-slate-300">{m.text}</p>
              </div>
            </div>
          ))}
          {comments.length === 0 && <p className="text-slate-500 text-sm">Be the first to comment.</p>}
        </div>
      </div>
    </div>
  );
}
